import { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { parseDeviceNavFrom, pathForSource } from '../utils/device-navigation';
import { ChevronLeft, ChevronRight, Trash2, Unlink, Link, Edit2 } from 'lucide-react';
import { Breadcrumb } from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Modal } from '@/components/ui/modal';
import { Select } from '@/components/ui/select';
import { LoadingPage } from '@/components/ui/loading';
import { getDevice, getDeviceMeterReadings, getDevices, deleteDevice, updateDevice, unassignDevice } from '../api/devices-api';
import { getGateways } from '@/features/gateways/api/gateways-api';
import { getUnits } from '@/features/units/api/units-api';
import { sortUnitsForDisplay } from '@/features/units/utils/sort-units-for-display';
import type { Unit } from '@/types/api';
import { useDataCache, makePaginatedCacheKey } from '@/store/data-cache-context';
import { usePagePolling, POLL_INTERVAL_DETAIL_MS } from '@/lib/use-page-polling';
import { useActiveRole } from '@/features/auth/hooks/use-active-role';
import { cn } from '@/lib/utils';
// Usage tile content lives inside DeviceDetailsTab now — May 15 pulled the
// six-tile stats row in favor of leading the Dashboard with the
// device-info layout. The component file is preserved in case the
// stats grid comes back later.
// DeviceDashboardTab (recharts) intentionally not imported — the May 15
// device-detail tweak parked the charts in favor of the details panel
// at the top of the Dashboard tab. Re-add the import if/when charts
// come back.
import { DeviceReadingsTab } from './device-readings-tab';
import { DeviceDetailsTab } from './device-details-tab';
import { DeviceEditModal } from './device-edit-modal';
import { SaltQuickConfigBanner } from './salt-quick-config-banner';
import { hasSaltConfig } from '../utils/salt-config';
import { ExportDataDropdown } from '@/features/properties/components/export-data-dropdown';
import { UserRole, DeviceType } from '@/types/api';
import type { Device, MeterReading, Gateway } from '@/types/api';

const PER_PAGE = 15;
const CHART_READINGS_LIMIT = 500;

type TabId = 'dashboard' | 'readings';

export function DeviceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Source page is captured at link time so we can route the user back to
  // the property/unit they came from after destructive actions like delete.
  // Falls back to /devices when missing or malformed.
  const navSource = parseDeviceNavFrom(searchParams.get('from'));
  const activeRole = useActiveRole();
  const canWrite = activeRole === UserRole.Owner || activeRole === UserRole.Admin || activeRole === UserRole.Manager;
  const canDelete = activeRole === UserRole.Owner || activeRole === UserRole.Admin;
  const { getMeterReadingsPage, setMeterReadingsPage, invalidate } = useDataCache();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [confirmUnassign, setConfirmUnassign] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignUnits, setAssignUnits] = useState<Unit[]>([]);
  const [assignUnitId, setAssignUnitId] = useState('');
  const [assignLoading, setAssignLoading] = useState(false);

  const [device, setDevice] = useState<Device | null>(null);
  const [deviceLoading, setDeviceLoading] = useState(true);

  // Readings for the paginated table
  const [readings, setReadings] = useState<MeterReading[]>([]);
  const [readingsLoading, setReadingsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Readings for charts + the slim usage-tiles row under the Device
  // Info card. The larger batch is used by `usageSince` to compute the
  // MTD delta fallback when no server-fetched boundary reading exists
  // (device started transmitting mid-month).
  const [chartReadings, setChartReadings] = useState<MeterReading[]>([]);

  // Boundary reading — the most recent reading at-or-before the start
  // of the current month. Powers the exact MTD delta on the "Total Usage
  // this month" tile.
  const [boundaryMonth, setBoundaryMonth] = useState<MeterReading | null>(null);

  // Sibling devices for meter navigation
  const [siblingDevices, setSiblingDevices] = useState<Device[]>([]);

  // Gateways for linking in transmission details
  const [propertyGateways, setPropertyGateways] = useState<Gateway[]>([]);

  // Active tab
  const [activeTab, setActiveTab] = useState<TabId>('dashboard');

  // Bumped after a save (e.g. SALT calibration banner) so the detail
  // load effect re-runs and the page picks up the new config without a
  // hard refresh.
  const [deviceRefresh, setDeviceRefresh] = useState(0);

  // Load device metadata
  useEffect(() => {
    if (!id) return;
    setDeviceLoading(true);
    getDevice(id)
      .then((res) => {
        if (res.success && res.data) setDevice(res.data);
      })
      .catch(() => {})
      .finally(() => setDeviceLoading(false));
  }, [id, deviceRefresh]);

  // Load sibling devices for navigation arrows
  useEffect(() => {
    if (!device?.unitId) {
      setSiblingDevices([]);
      return;
    }
    getDevices({ unitId: device.unitId, perPage: 100 })
      .then((res) => setSiblingDevices(res.items))
      .catch(() => {});
  }, [device?.unitId]);

  // Load gateways for the device's property (used in reading detail modal)
  useEffect(() => {
    if (!device?.propertyId) {
      setPropertyGateways([]);
      return;
    }
    getGateways({ propertyId: device.propertyId, perPage: 100 })
      .then((res) => setPropertyGateways(res.items))
      .catch(() => {});
  }, [device?.propertyId]);

  // Load readings page (with cache). Listens to deviceRefresh so a calibration
  // change (Edit Modal / SALT banner) bypasses the cache and reflects the
  // updated computed values immediately.
  useEffect(() => {
    if (!id) return;
    const cacheKey = makePaginatedCacheKey({
      deviceId: id,
      page: currentPage,
      perPage: PER_PAGE,
    });
    const cached = deviceRefresh === 0 ? getMeterReadingsPage(cacheKey) : null;
    if (cached) {
      setReadings(cached.items);
      setTotalCount(cached.totalCount);
      setTotalPages(cached.totalPages);
      setReadingsLoading(false);
      return;
    }
    setReadingsLoading(true);
    getDeviceMeterReadings(id, { page: currentPage, perPage: PER_PAGE })
      .then((res) => {
        setMeterReadingsPage(cacheKey, res);
        setReadings(res.items);
        setTotalCount(res.totalCount);
        setTotalPages(res.totalPages);
      })
      .catch(() => setReadings([]))
      .finally(() => setReadingsLoading(false));
  }, [id, currentPage, deviceRefresh]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load chart readings (larger batch for visualization). Same deviceRefresh
  // dependency so charts + stats refresh after a calibration save.
  useEffect(() => {
    if (!id) return;
    getDeviceMeterReadings(id, { page: 1, perPage: CHART_READINGS_LIMIT })
      .then((res) => setChartReadings(res.items))
      .catch(() => setChartReadings([]));
  }, [id, deviceRefresh]);

  // Load the boundary reading for exact month-to-date delta. The most
  // recent reading at-or-before the start of the current month — cheap
  // single-row fetch. Re-fired on calibration save so an offset edit
  // flows through to the stats cards without a hard refresh.
  useEffect(() => {
    if (!id) return;
    const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString();
    getDeviceMeterReadings(id, { page: 1, perPage: 1, before: monthStart })
      .then((res) => setBoundaryMonth(res.items[0] ?? null))
      .catch(() => setBoundaryMonth(null));
  }, [id, deviceRefresh]);

  // Background poll: visibility-gated detail-page cadence. Refreshes the
  // device metadata, the readings table for the current page, and the
  // chart batch silently — no spinners between polls.
  usePagePolling(
    () => {
      if (!id) return;
      getDevice(id)
        .then((res) => {
          if (!res.success || !res.data) return;
          // Backend derives propertyId/propertyName via Unit→Building→Property,
          // so an unassigned device returns nulls there. Preserve the locally-
          // known values across the poll so the breadcrumb's property link
          // doesn't vanish ~10s after an unassign and leave the installer
          // looking like they were redirected to /properties. Drop this once
          // Device.PropertyId is a direct field on the entity (separate work).
          setDevice((prev) => {
            const next = res.data!;
            if (!next.propertyId && prev?.propertyId) {
              return { ...next, propertyId: prev.propertyId, propertyName: prev.propertyName };
            }
            return next;
          });
        })
        .catch(() => {});
      getDeviceMeterReadings(id, { page: currentPage, perPage: PER_PAGE })
        .then((res) => {
          setReadings(res.items);
          setTotalCount(res.totalCount);
          setTotalPages(res.totalPages);
        })
        .catch(() => {});
      getDeviceMeterReadings(id, { page: 1, perPage: CHART_READINGS_LIMIT })
        .then((res) => setChartReadings(res.items))
        .catch(() => {});
    },
    POLL_INTERVAL_DETAIL_MS,
    { enabled: !!id },
  );

  async function handleDeleteConfirm() {
    if (!id) return;
    setIsDeleting(true);
    try {
      await deleteDevice(id);
      invalidate(['devices', 'units', 'properties', 'meter-readings', 'alerts']);
      // If the user navigated here from a property or unit page (?from=...),
      // route them back to that page so they keep their place. Source is
      // optimistic — if the property/unit was soft-deleted in another tab,
      // its detail page handles the missing-resource state on its own.
      navigate(navSource ? pathForSource(navSource) : '/devices');
    } catch (error) {
      console.error('Failed to delete device:', error);
    } finally {
      setIsDeleting(false);
      setConfirmDelete(false);
    }
  }

  // Force-refresh the device snapshot before opening the edit modal so
  // the inputs (especially the auto-captured Mechanical Offset, which
  // gets written server-side on first transmission) reflect the latest
  // persisted values rather than whatever the last poll happened to
  // load. The poll cadence is 5s, so without this refresh a user who
  // opens the modal seconds after a transmission lands can see an
  // empty offset field even though the captured value is in the DB.
  async function openEditModal() {
    setShowEditModal(true);
    if (!id) return;
    try {
      const res = await getDevice(id);
      if (res.success && res.data) setDevice(res.data);
    } catch {
      // Soft-fail: modal opens with the existing snapshot. The
      // belt-and-suspenders useEffect inside the modal will sync
      // again whenever a subsequent poll refreshes `device`.
    }
  }

  async function handleUnassign() {
    if (!id) return;
    setConfirmUnassign(false);
    try {
      await unassignDevice(id);
      setDevice(prev => prev ? { ...prev, unitId: undefined, unitNumber: undefined, unitAssignedAt: undefined } : prev);
      invalidate(['devices', 'units', 'properties']);
    } catch {
      // silent
    }
  }

  async function openAssignModal() {
    // Pre-select the current unit when reassigning, leave empty when this
    // is a fresh assign (the device has no unit yet). Modal title and
    // submit-button label switch on the same condition below.
    // TODO(future): scope `getUnits` to the current property's buildings
    // — backend `/api/units` only filters by buildingId, so a property-
    // scoped picker requires either iterating buildings or extending the
    // controller. Out of scope for the May 7 batch.
    setAssignUnitId(device?.unitId ?? '');
    setShowAssignModal(true);
    try {
      const res = await getUnits({ perPage: 100 });
      // Server returns units in creation order, which mixes buildings
      // and unit-number ranges unpredictably in the picker. Group by
      // building name (A→Z) and apply the same natural-numeric sort
      // (1, 2, 2A, 10, "Penthouse") inside each group that the property
      // sidebar uses.
      const byBuilding = new Map<string, Unit[]>();
      for (const u of res.items) {
        const key = u.buildingName ?? '';
        const list = byBuilding.get(key) ?? [];
        list.push(u);
        byBuilding.set(key, list);
      }
      const sorted = [...byBuilding.entries()]
        .sort(([a], [b]) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
        .flatMap(([, units]) => sortUnitsForDisplay(units));
      setAssignUnits(sorted);
    } catch {
      setAssignUnits([]);
    }
  }

  async function handleAssign() {
    if (!id || !assignUnitId) return;
    setAssignLoading(true);
    try {
      await updateDevice(id, { unitId: assignUnitId });
      // Reload device to get updated unit info
      const res = await getDevice(id);
      if (res.success && res.data) setDevice(res.data);
      invalidate(['devices', 'units', 'properties']);
      setShowAssignModal(false);
    } catch {
      // silent
    } finally {
      setAssignLoading(false);
    }
  }

  if (deviceLoading) return <LoadingPage />;

  if (!device) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
          <p className="text-gray-500">Device not found.</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate(-1)}>
            Back
          </Button>
        </div>
      </div>
    );
  }

  // Meter navigation
  const currentIndex = siblingDevices.findIndex((d) => d.id === device.id);
  const prevDevice = currentIndex > 0 ? siblingDevices[currentIndex - 1] : null;
  const nextDevice = currentIndex < siblingDevices.length - 1 ? siblingDevices[currentIndex + 1] : null;

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        <Breadcrumb items={[
          { label: 'Properties', href: '/properties' },
          ...(device.propertyId ? [{ label: device.propertyName ?? 'Property', href: `/properties/${device.propertyId}` }] : []),
          ...(device.unitId ? [{ label: `Unit ${device.unitNumber}`, href: `/units/${device.unitId}` }] : []),
          { label: device.serialNumber },
        ]} />

        {/* Header with meter navigation. Stacks vertically on phones so the
            action buttons drop to a new line below the serial-number heading
            instead of crowding it; collapses into a single row from `sm` up. */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {siblingDevices.length > 1 && (
              <button
                onClick={() => prevDevice && navigate(`/devices/${prevDevice.id}`)}
                disabled={!prevDevice}
                className={cn(
                  'p-1 rounded transition-colors',
                  prevDevice ? 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800' : 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                )}
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
            )}
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {device.serialNumber}
              </h1>
              {device.unitId && device.unitNumber ? (
                <button
                  className="text-sm text-primary-600 dark:text-primary-400 hover:underline mt-0.5"
                  onClick={() => navigate(`/units/${device.unitId}`)}
                >
                  {device.unitNumber}
                </button>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Unassigned</p>
              )}
            </div>
            {siblingDevices.length > 1 && (
              <button
                onClick={() => nextDevice && navigate(`/devices/${nextDevice.id}`)}
                disabled={!nextDevice}
                className={cn(
                  'p-1 rounded transition-colors',
                  nextDevice ? 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800' : 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                )}
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            )}
          </div>
          {canWrite && (
            <div className="flex items-center gap-2 flex-wrap">
              <Button variant="outline" onClick={openEditModal}>
                <Edit2 className="w-4 h-4" />
                Edit
              </Button>
              <ExportDataDropdown deviceId={device.id} />
              {device.unitId ? (
                <>
                  <Button variant="outline" onClick={openAssignModal}>
                    <Link className="w-4 h-4" />
                    Change Unit
                  </Button>
                  <Button variant="outline" onClick={() => setConfirmUnassign(true)}>
                    <Unlink className="w-4 h-4" />
                    Unassign
                  </Button>
                </>
              ) : (
                <Button variant="outline" onClick={openAssignModal}>
                  <Link className="w-4 h-4" />
                  Assign to Unit
                </Button>
              )}
              {canDelete && (
                <Button variant="danger" onClick={() => setConfirmDelete(true)}>
                  <Trash2 className="w-4 h-4" />
                  Delete
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Usage facts (Total Usage this month / Raw / Last Active)
            are baked into the bottom of the Device Info card now —
            see DeviceDetailsTab. The May 15 batch deliberately dropped
            the floating "top grid buttons" treatment. */}

        {/* SALT calibration prompt — only when this is a SALT transmitter
            and no pulse-encoder config has been saved yet. Setup Mode
            doesn't collect this anymore (the dropdown was wrong half the
            time), so we nudge the installer here instead. */}
        {device.deviceType === DeviceType.SALTTransmitter && !hasSaltConfig(device) && canWrite && (
          <SaltQuickConfigBanner
            device={device}
            onSaved={() => setDeviceRefresh((n) => n + 1)}
          />
        )}

        {/* Tabs — Dashboard surfaces the device-information panel at the
            top so installers see identity + connectivity context first.
            Charts have been pulled for now (the recharts panel wasn't
            adding value on a per-device page); the Readings tab still
            carries the full transmission history. */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex flex-wrap gap-x-6 gap-y-2">
            {([
              { id: 'dashboard' as const, label: 'Dashboard' },
              { id: 'readings' as const, label: `Readings (${totalCount})` },
            ]).map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  'pb-3 text-sm font-medium transition-colors',
                  activeTab === tab.id
                    ? 'text-primary-600 border-b-2 border-primary-600'
                    : 'text-gray-500 hover:text-gray-700'
                )}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'dashboard' && (
          <DeviceDetailsTab
            device={device}
            gateways={propertyGateways}
            readings={chartReadings}
            boundaryMonth={boundaryMonth}
            onEdit={canWrite ? openEditModal : undefined}
          />
        )}
        {activeTab === 'readings' && (
          <DeviceReadingsTab
            device={device}
            readings={readings}
            totalCount={totalCount}
            totalPages={totalPages}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            loading={readingsLoading}
            gateways={propertyGateways}
          />
        )}
      </div>

      <ConfirmDialog
        isOpen={confirmDelete}
        title="Delete Device"
        description={`Are you sure you want to delete device "${device.serialNumber}"? This action cannot be undone.`}
        confirmLabel="Delete"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setConfirmDelete(false)}
        isLoading={isDeleting}
      />

      <ConfirmDialog
        isOpen={confirmUnassign}
        title="Unassign from Unit"
        description={`Unassign "${device.serialNumber}" from Unit ${device.unitNumber}? Readings from before reassignment will no longer be visible for this unit.`}
        confirmLabel="Unassign"
        onConfirm={handleUnassign}
        onCancel={() => setConfirmUnassign(false)}
      />

      <Modal
        isOpen={showAssignModal}
        onClose={() => setShowAssignModal(false)}
        title={device.unitId ? 'Change Unit' : 'Assign to Unit'}
        size="sm"
      >
        <div className="space-y-4">
          <Select
            label="Unit"
            value={assignUnitId}
            onChange={setAssignUnitId}
            options={[
              { value: '', label: 'Select a unit...' },
              ...assignUnits.map(u => ({
                value: u.id,
                label: `${u.buildingName} — Unit ${u.unitNumber}`,
              })),
            ]}
            searchable
          />
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={() => setShowAssignModal(false)}>Cancel</Button>
            <Button
              onClick={handleAssign}
              // Disable when nothing is selected OR when the current
              // assignment is unchanged (no point hitting the API).
              disabled={!assignUnitId || assignUnitId === device.unitId}
              isLoading={assignLoading}
            >
              {device.unitId ? 'Save' : 'Assign'}
            </Button>
          </div>
        </div>
      </Modal>

      {showEditModal && (
        <DeviceEditModal
          device={device}
          onClose={() => setShowEditModal(false)}
          onSave={async (data) => {
            await updateDevice(id!, data);
            // meter-readings cache must invalidate too — a calibration
            // change (multiplier / UoM / mechanical offset) shifts every
            // reading's computed value via the read-time transform.
            invalidate(['devices', 'meter-readings']);
            // Re-fetch device + readings + chartReadings inline so the
            // page reflects the new calibration before the modal closes.
            // We also bump deviceRefresh as a backup so any other surface
            // that depends on it (or future poll cycles that hit cache)
            // bypasses cache too.
            const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString();
            const [deviceRes, readingsRes, chartRes, bMonth] = await Promise.all([
              getDevice(id!),
              getDeviceMeterReadings(id!, { page: currentPage, perPage: PER_PAGE }),
              getDeviceMeterReadings(id!, { page: 1, perPage: CHART_READINGS_LIMIT }),
              getDeviceMeterReadings(id!, { page: 1, perPage: 1, before: monthStart }),
            ]);
            if (deviceRes.success && deviceRes.data) setDevice(deviceRes.data);
            setReadings(readingsRes.items);
            setTotalCount(readingsRes.totalCount);
            setTotalPages(readingsRes.totalPages);
            setChartReadings(chartRes.items);
            setBoundaryMonth(bMonth.items[0] ?? null);
            setDeviceRefresh((n) => n + 1);
            setShowEditModal(false);
          }}
        />
      )}
    </div>
  );
}
